import { DEFAULT_FIELDS } from 'schemas/default-fields';
import { DATASOURCE, FIELD_TYPE, Schema } from 'schemas/types';
import {
  DATA_SOURCE_NAME_FIELD,
  EVENT_CATEGORY_FIELD,
  EVENT_DETAILS_FIELD,
  EVENT_SOURCE_FIELD,
  EVENT_TARGET_FIELD,
} from 'schemas/constants';
import { EVENT_DETAILS_FIELDS_BY_CATEGORY } from './event-details-fields-by-category';
import { SUMMARY_FIELDS_BY_CATEGORY } from './summary-fields-by-category';

export const schema: Schema = {
  dataSource: DATASOURCE.SENTINEL_ONE,
  dataSourceId: 'SentinelOne',
  dataSourceVendor: 'SentinelOne',
  // DPVZ-2313 We reserve the name SentinelOne for the agent, others like Ranger will use their name
  dataSourceLabel: 'SentinelOne',
  fields: {
    ...DEFAULT_FIELDS,
    'dst.ip.address': {
      type: FIELD_TYPE.IP_ADDRESS,
      label: 'Destination IP',
      description: 'IP address of traffic destination',
    },
    'dst.port.number': {
      type: FIELD_TYPE.NUMBER,
      label: 'Destination Port',
      description: 'Port number of traffic destination',
    },
    [EVENT_SOURCE_FIELD]: {
      label: 'Event Source',
      type: FIELD_TYPE.EVENT_SOURCE,
      notSearchable: true,
      relatedFields: [
        'agent.uuid',
        'src.endpoint.ip.address',
        'src.process.uid',
        'event.login.userName',
        'src.ip.address',
      ],
      width: 540, // (160 + 20) x 3
    },
    [EVENT_TARGET_FIELD]: {
      label: 'Event Target',
      type: FIELD_TYPE.EVENT_TARGET,
      notSearchable: true,
      relatedFields: [
        'tgt.file.path',
        'task.name',
        'dst.ip.address',
        'tgt.process.uid',
        'url.address',
        'event.dns.request',
        'registry.keyPath',
        'module.path',
      ],
      width: 540, // (160 + 20) x 3
    },
    'agent.uuid': {
      label: 'Endpoint',
      type: FIELD_TYPE.ENDPOINT,
      relatedFields: [
        'endpoint.name', // needed to fetch endpoint card details
        'endpoint.os', // needed for the icon
        'endpoint.type', // needed for the icon
        DATA_SOURCE_NAME_FIELD, // needed to fetch endpoint card details
        'mgmt.url', // needed to fetch endpoint card details
      ],
    },
    'endpoint.name': {
      label: 'Endpoint Name',
      type: FIELD_TYPE.TEXT,
    },
    'endpoint.os': {
      label: 'Endpoint OS',
      type: FIELD_TYPE.ENUM,
      description: 'Endpoint OS: windows, osx, linux',
      values: ['linux', 'macos', 'osx', 'windows', 'windows-legacy'],
      icon: {
        // without type
        linux: 'linux',
        macos: 'mac',
        osx: 'mac',
        windows_legacy: 'windows-legacy',
        windows: 'windows',
        // laptop icons
        'laptop-linux': 'laptop-linux',
        'laptop-macos': 'laptop-mac',
        'laptop-osx': 'laptop-mac',
        'laptop-windows_legacy': 'laptop-windows-legacy',
        'laptop-windows': 'laptop-windows',
        // desktop icons
        'desktop-linux': 'desktop-linux',
        'desktop-macos': 'desktop-mac',
        'desktop-osx': 'desktop-mac',
        'desktop-windows_legacy': 'desktop-windows-legacy',
        'desktop-windows': 'desktop-windows',
        // server icons
        'server-linux': 'server-linux',
        'server-macos': 'server-mac',
        'server-osx': 'server-mac',
        'server-windows_legacy': 'server-windows-legacy',
        'server-windows': 'server-windows',
      },
    },
    'endpoint.type': {
      type: FIELD_TYPE.ENUM,
      label: 'Endpoint Machine Type',
      description: 'Machine type: server, laptop, desktop, Kubernetes Node',
      values: ['Desktop', 'Kubernetes Node', 'Laptop', 'Server', 'Unknown'],
    },
    [EVENT_DETAILS_FIELD]: {
      type: FIELD_TYPE.EVENT_DETAILS,
      label: 'Event Details',
      description: 'Event additional details',
      notSearchable: true,
      width: 900, // (160 + 20) x 5,
      relatedFieldsMap: EVENT_DETAILS_FIELDS_BY_CATEGORY,
    },
    'event.dns.request': {
      type: FIELD_TYPE.DNS,
      label: 'DNS Request',
      description: 'DNS name',
    },
    'event.dns.response': {
      type: FIELD_TYPE.TEXT,
      label: 'DNS Response',
      description: 'IP address, DNS, type, etc. in response',
    },
    'event.id': {
      type: FIELD_TYPE.TEXT,
      label: 'Event ID',
      description: 'Unique SentinelOne ID of event',
    },
    'event.login.accountDomain': {
      type: FIELD_TYPE.TEXT,
      label: 'Account Domain',
      description: 'Domain or computer name for which login attempt was performed',
    },
    'event.login.accountName': {
      type: FIELD_TYPE.TEXT,
      label: 'Account Name',
      description: 'Account login name for which login attempt was performed',
    },
    'event.login.accountSid': {
      type: FIELD_TYPE.TEXT,
      label: 'Account SID',
      description: 'SID of the account that attempted to login',
    },
    'event.login.baseType': {
      type: FIELD_TYPE.TEXT,
      label: 'Logins Base type',
      description: 'Logins base type',
    },
    'event.login.failureReason': {
      type: FIELD_TYPE.TEXT,
      label: 'Failure Reason',
      description: 'Login failure reason',
    },
    'event.login.isAdministratorEquivalent': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'Is administrator equivalent',
      description: 'Is the login attempt administrator equivalent',
    },
    'event.login.loginIsSuccessful': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'Login is Successful',
      description: 'Was the login attempt successful',
    },
    'event.login.sessionId': {
      type: FIELD_TYPE.NUMBER,
      label: 'Session ID',
      description: 'Session ID of the successful login',
    },
    'event.login.type': {
      type: FIELD_TYPE.ENUM,
      label: 'Type',
      description: 'Type of login which was performed',
      values: [
        'BATCH',
        'CACHED_INTERACTIVE',
        'CACHED_REMOTE_INTERACTIVE',
        'CACHED_UNLOCK',
        'INTERACTIVE',
        'NETWORK',
        'NETWORK_CLEAR_TEXT',
        'NETWORK_CREDENTIALS',
        'REMOTE_INTERACTIVE',
        'SERVICE',
        'SYSTEM',
        'UNLOCK',
      ],
    },
    'event.login.userName': {
      type: FIELD_TYPE.USER,
      label: 'Logins User Name',
      description: 'Logins User Name',
    },
    'event.network.connectionStatus': {
      type: FIELD_TYPE.ENUM,
      label: 'Network Connection Status',
      description: 'Network event status',
      values: ['Blocked', 'Failure', 'Success', 'Unknown'],
    },
    'event.network.direction': {
      type: FIELD_TYPE.ENUM,
      label: 'Network Event Direction',
      description: 'Direction of the connection',
      values: ['INCOMING', 'OUTGOING'],
    },
    'event.network.protocolName': {
      type: FIELD_TYPE.TEXT,
      label: 'Network Protocol Name',
      description: 'Protocol name per IANA well known ports distribution',
    },
    'event.url.action': {
      type: FIELD_TYPE.ENUM,
      label: 'URL Action',
      description: 'URL action of process',
      values: ['CONNECT', 'DELETE', 'GET', 'HEAD', 'OPTIONS', 'POST', 'PUT'],
    },
    'indicator.category': {
      type: FIELD_TYPE.ENUM,
      label: 'Indicator Category',
      description: 'Category name of the indicator',
      values: [
        'Abnormalities',
        'Boot Configuration Update',
        'Evasion',
        'Exploitation',
        'General',
        'Hiding/Stealthiness',
        'InfoStealer',
        'Injection',
        'Malware',
        'Packer',
        'Persistence',
        'Post Exploitation',
        'Privilege Escalation',
        'Ransomware',
        'Reconnaissance',
      ],
    },
    'indicator.description': {
      type: FIELD_TYPE.TEXT,
      label: 'Indicator Description',
      description: 'Description of the indicator',
    },
    'indicator.metadata': {
      type: FIELD_TYPE.TEXT,
      label: 'Indicator Metadata',
      description: 'Metadata of the indicator',
    },
    'indicator.name': {
      type: FIELD_TYPE.TEXT,
      label: 'Indicator Name',
      description: 'Indicator name',
    },
    'k8sCluster.containerId': {
      type: FIELD_TYPE.TEXT,
      label: 'Container ID',
      description: 'Container ID',
    },
    'k8sCluster.containerImage': {
      type: FIELD_TYPE.TEXT,
      label: 'Container Image',
      description: 'Container image',
    },
    'k8sCluster.containerLabels': {
      type: FIELD_TYPE.TEXT,
      label: 'Container Labels',
      description: 'Container labels',
    },
    'k8sCluster.containerName': {
      type: FIELD_TYPE.TEXT,
      label: 'Container Name',
      description: 'Container name',
    },
    'k8sCluster.controllerLabels': {
      type: FIELD_TYPE.TEXT,
      label: 'K8s Controller Labels',
      description: 'Kubernetes controller labels',
    },
    'k8sCluster.controllerName': {
      type: FIELD_TYPE.TEXT,
      label: 'K8s Controller Name',
      description: 'Kubernetes controller name',
    },
    'k8sCluster.controllerType': {
      type: FIELD_TYPE.TEXT,
      label: 'K8s Controller Type',
      description: 'Kubernetes controller type',
    },
    'k8sCluster.name': {
      type: FIELD_TYPE.TEXT,
      label: 'K8s Cluster Name',
      description: 'Kubernetes cluster name',
    },
    'k8sCluster.namespace': {
      type: FIELD_TYPE.TEXT,
      label: 'K8s Namespace',
      description: 'Kubernetes namespace',
    },
    'k8sCluster.namespaceLabels': {
      type: FIELD_TYPE.TEXT,
      label: 'K8s Namespace Labels',
      description: 'Kubernetes namespace labels',
    },
    'k8sCluster.nodeName': {
      type: FIELD_TYPE.TEXT,
      label: 'K8s Node Name',
      description: 'Kubernetes node name',
    },
    'k8sCluster.podLabels': {
      type: FIELD_TYPE.TEXT,
      label: 'K8s Pod Labels',
      description: 'Kubernetes pod labels',
    },
    'k8sCluster.podName': {
      type: FIELD_TYPE.TEXT,
      label: 'K8s Pod Name',
      description: 'Kubernetes pod name',
    },
    'module.md5': {
      type: FIELD_TYPE.HASH,
      label: 'Module Image MD5',
      description: 'Module MD5 Signature',
    },
    'module.path': {
      type: FIELD_TYPE.MODULE,
      label: 'Module Path',
      description: 'Module Path',
    },
    'module.sha1': {
      type: FIELD_TYPE.HASH,
      label: 'Module Image SHA1',
      description: 'Module SHA1 Signature',
    },
    'osSrc.process.activeContent.hash': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Active Content Hash',
      description: 'Active Content SHA1 of source process (as attributed by the OS)',
    },
    'osSrc.process.activeContent.id': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Active Content File ID',
      description: 'Active Content file unique ID of source process (as attributed by the OS)',
    },
    'osSrc.process.activeContent.path': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Active Content Path',
      description: 'Active Content file path of source process (as attributed by the OS)',
    },
    'osSrc.process.activeContent.signedStatus': {
      type: FIELD_TYPE.ENUM,
      label: 'OS Source Process Active Content Signed Status',
      description: 'Active Content file signed status of source process (as attributed by the OS)',
      values: ['signed', 'unsigned'],
    },
    'osSrc.process.activeContentType': {
      type: FIELD_TYPE.ENUM,
      label: 'OS Source Process Active Content Type',
      description: 'Active Content type of source process (as attributed by the OS)',
      values: ['CLI', 'FILE'],
    },
    'osSrc.process.childProcCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Child Process Count',
      description: 'Child process count',
    },
    'osSrc.process.cmdline': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Command Line',
      description: 'Command arguments sent with source process (as attributed by the OS)',
    },
    'osSrc.process.crossProcessCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Cross Process Count',
      description: 'Target Process event Count',
    },
    'osSrc.process.crossProcessDupRemoteProcessHandleCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Cross Process Duplicate Process Handle Count',
      description: 'Duplicate Process Handle event count',
    },
    'osSrc.process.crossProcessDupThreadHandleCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Cross Process Duplicate Thread Handle Count',
      description: 'Duplicate Thread Handle Count',
    },
    'osSrc.process.crossProcessOpenProcessCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Cross Process Open Process Count',
      description: 'Open Process Count',
    },
    'osSrc.process.crossProcessOutOfStorylineCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Cross Process Out of Storyline Count',
      description: 'Out of Storyline event count',
    },
    'osSrc.process.crossProcessThreadCreateCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Cross Process Thread Create Count',
      description: 'Remote Thread Count',
    },
    'osSrc.process.displayName': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Display name',
      description: 'Display name of source process (as attributed by the OS)',
    },
    'osSrc.process.dnsCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS DNS Requests Count',
      description: 'Count of DNS requests',
    },
    'osSrc.process.image.binaryIsExecutable': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'OS Source Process Binary is Executable',
      description: 'Is binary backing source process (as attributed by the OS) an executable',
    },
    'osSrc.process.image.md5': {
      type: FIELD_TYPE.HASH,
      label: 'OS Source Process Image MD5',
      description: 'MD5 of source process (as attributed by the OS)',
    },
    'osSrc.process.image.path': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Image path',
      description: 'Image path of source process (as attributed by the OS)',
    },
    'osSrc.process.image.sha1': {
      type: FIELD_TYPE.HASH,
      label: 'OS Source Process Image SHA1',
      description: 'SHA1 of source process (as attributed by the OS)',
    },
    'osSrc.process.image.sha256': {
      type: FIELD_TYPE.HASH,
      label: 'OS Source Process Image SHA256',
      description: 'SHA256 of source process (as attributed by the OS)',
    },
    'osSrc.process.indicatorBootConfigurationUpdateCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Indicator Boot Configuration Update Count',
      description: 'Count of indicators - Boot Configuration Update',
    },
    'osSrc.process.indicatorEvasionCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Indicator Evasion Count',
      description: 'Count of indicators - Evasion',
    },
    'osSrc.process.indicatorExploitationCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Indicator Exploitation Count',
      description: 'Count of indicators - Exploitation',
    },
    'osSrc.process.indicatorGeneral.count': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Indicator General Count',
      description: 'Count of indicators - General',
    },
    'osSrc.process.indicatorInfostealerCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Indicator Infostealer Count',
      description: 'Count of indicators - Infostealer',
    },
    'osSrc.process.indicatorInjectionCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Indicator Injection Count',
      description: 'Count of indicators - Injection',
    },
    'osSrc.process.indicatorPersistenceCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Indicator Persistence Count',
      description: 'Count of indicators - Persistence',
    },
    'osSrc.process.indicatorPostExploitationCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Indicator Post Exploitation Count',
      description: 'Count of indicators - Post Exploitation',
    },
    'osSrc.process.indicatorRansomwareCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Indicator Ransomware Count',
      description: 'Count of indicators - Ransomware',
    },
    'osSrc.process.indicatorReconnaissanceCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Indicator Reconnaissance Count',
      description: 'Count of indicators - Reconnaissance',
    },
    'osSrc.process.integrityLevel': {
      type: FIELD_TYPE.ENUM,
      label: 'OS Source Process Integrity level',
      description: 'Integrity level of source process (as attributed by the OS)',
      values: ['HIGH', 'INTEGRITY_LEVEL_UNKNOWN', 'LOW', 'MEDIUM', 'SYSTEM', 'UNTRUSTED'],
    },
    'osSrc.process.isNative64Bit': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'OS Source Process Is Native 64Bit',
      description: 'Is source process (as attributed by the OS) compiled natively for 64-Bit or runs as WoW',
    },
    'osSrc.process.isRedirectCmdProcessor': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'OS Source Process Is Redirected Command Processor',
      description: 'Is interpreter for source process (as attributed by the OS) with stdout redirection',
    },
    'osSrc.process.isStorylineRoot': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'OS Source Process Is OS Root',
      description: 'Is source process (as attributed by the OS) root of the Storyline',
    },
    'osSrc.process.moduleCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Module Count',
      description: 'Count of Modules Loaded',
    },
    'osSrc.process.name': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Name',
      description: 'Name of source process (as attributed by the OS)',
    },
    'osSrc.process.netConnCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Network Connections Count',
      description: 'Network connection count',
    },
    'osSrc.process.netConnInCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Network Connection Incoming Count',
      description: 'Incoming network connection count',
    },
    'osSrc.process.netConnOutCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Network Connection Outgoing Count',
      description: 'Outgoing network connection count',
    },
    'osSrc.process.parent.activeContent.hash': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Parent Active Content Hash',
      description: 'Active Content SHA1 for the OS source process parent (as attributed by the OS)',
    },
    'osSrc.process.parent.activeContent.id': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Parent Active Content File ID',
      description: 'Active Content file unique ID for the OS source process parent (as attributed by the OS)',
    },
    'osSrc.process.parent.activeContent.path': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Parent Active Content Path',
      description: 'Active Content file path for the OS source process parent (as attributed by the OS)',
    },
    'osSrc.process.parent.activeContent.signedStatus': {
      type: FIELD_TYPE.ENUM,
      label: 'OS Source Process Parent Active Content Signed Status',
      description: 'Active Content file signed status for the OS source process parent (as attributed by the OS)',
      values: ['signed', 'unsigned'],
    },
    'osSrc.process.parent.activeContentType': {
      type: FIELD_TYPE.ENUM,
      label: 'OS Source Process Parent Active Content Type',
      description: 'Active Content type of the OS source process parent (as attributed by the OS)',
      values: ['CLI', 'FILE'],
    },
    'osSrc.process.parent.cmdline': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Parent Command Line',
      description:
        'Command arguments sent with the process that created the OS source process (as attributed by the OS)',
    },
    'osSrc.process.parent.displayName': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Parent Display name',
      description: 'Display name of process that created the OS source process (as attributed by the OS)',
    },
    'osSrc.process.parent.image.md5': {
      type: FIELD_TYPE.HASH,
      label: 'OS Source Parent Process Image MD5',
      description: 'MD5 of process that created the OS source process (as attributed by the OS)',
    },
    'osSrc.process.parent.image.path': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Parent Process Image path',
      description: 'Image path of process that created the OS source process (as attributed by the OS)',
    },
    'osSrc.process.parent.image.sha1': {
      type: FIELD_TYPE.HASH,
      label: 'OS Source Parent Process Image SHA1',
      description: 'SHA1 of process that created the OS source process (as attributed by the OS)',
    },
    'osSrc.process.parent.image.sha256': {
      type: FIELD_TYPE.HASH,
      label: 'OS Source Parent Process Image SHA256',
      description: 'SHA256 of process that created the OS source process (as attributed by the OS)',
    },
    'osSrc.process.parent.integrityLevel': {
      type: FIELD_TYPE.ENUM,
      label: 'OS Source Process Parent Integrity level',
      description: 'Integrity level of process that created the OS source process (as attributed by the OS)',
      values: ['HIGH', 'INTEGRITY_LEVEL_UNKNOWN', 'LOW', 'MEDIUM', 'SYSTEM', 'UNTRUSTED'],
    },
    'osSrc.process.parent.isNative64Bit': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'OS Source Process Parent Is Native 64Bit',
      description:
        'Was the OS source process parent (as attributed by the OS) compiled natively for 64-Bit or runs as WoW',
    },
    'osSrc.process.parent.isRedirectCmdProcessor': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'OS Source Process Parent Is Redirected Command Processor',
      description: 'Does OS source process parent (as attributed by the OS) interpreter have stdout redirection',
    },
    'osSrc.process.parent.isStorylineRoot': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'OS Source Process Parent Is OS Root',
      description: 'Is OS source process parent (as attributed by the OS) root of Storyline',
    },
    'osSrc.process.parent.name': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Parent Process',
      description: 'Name of the process that created the OS source process (as attributed by the OS)',
    },
    'osSrc.process.parent.pid': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Source Parent PID',
      description: 'PID of process that created the OS source process (as attributed by the OS)',
    },
    'osSrc.process.parent.publisher': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Parent Publisher',
      description:
        'Publisher that signed binary invoked as part of the process that created the OS source process (as attributed by the OS)',
    },
    'osSrc.process.parent.reasonSignatureInvalid': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Parent Reason Signature Invalid',
      description: 'Reason process that created the OS source process signature is not valid (as attributed by the OS)',
    },
    'osSrc.process.parent.sessionId': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Source Process Parent Session ID',
      description:
        'ID of terminal (cmd, shell, other) session of the process that created the OS source process (as attributed by the OS)',
    },
    'osSrc.process.parent.signedStatus': {
      type: FIELD_TYPE.ENUM,
      label: 'OS Source Process Parent Signed Status',
      description: 'Signature status of process that created the OS source process (as attributed by the OS)',
      values: ['signed', 'unsigned'],
    },
    'osSrc.process.parent.startTime': {
      type: FIELD_TYPE.TIMESTAMP,
      label: 'OS Source Parent Process Start Time',
      description: 'Time process that created the OS source process started to run (as attributed by the OS)',
    },
    'osSrc.process.parent.storyline.id': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Parent OS ID',
      description: 'Storyline ID of the process that created the OS source process (as attributed by the OS)',
    },
    'osSrc.process.parent.uid': {
      type: FIELD_TYPE.PROCESS,
      label: 'OS Source Parent Process UID',
      description: 'Unique ID of process that created the OS source process (as attributed by the OS)',
      relatedFields: ['osSrc.process.parent.name'],
      width: 180,
    },
    'osSrc.process.parent.user': {
      type: FIELD_TYPE.USER,
      label: 'OS Source Process Parent User',
      description: 'Username under which the process that created the OS source process ran  (as attributed by the OS)',
    },
    'osSrc.process.pid': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Source Process ID (PID)',
      description: 'PID for source process (as attributed by the OS)',
    },
    'osSrc.process.publisher': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Publisher',
      description: 'Publisher that signed binary invoked as part of the process (as attributed by the OS)',
    },
    'osSrc.process.reasonSignatureInvalid': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Reason Signature Invalid',
      description: 'Reason process (as attributed by the OS) signature invalid',
    },
    'osSrc.process.registryChangeCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Registry Change Count',
      description: 'Count of registry key changes',
    },
    'osSrc.process.sessionId': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Source Process Session ID',
      description: 'ID of the terminal (cmd, shell, other) session of source process (as attributed by the OS)',
    },
    'osSrc.process.signedStatus': {
      type: FIELD_TYPE.ENUM,
      label: 'OS Source Process Signed Status',
      description: 'Signature status of source process (as attributed by the OS)',
      values: ['signed', 'unsigned'],
    },
    'osSrc.process.startTime': {
      type: FIELD_TYPE.TIMESTAMP,
      label: 'OS Source Process Start Time',
      description: 'Start time of source process (as attributed by the OS)',
    },
    'osSrc.process.storyline.id': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Storyline Id',
      description: 'Storyline ID of the source process (as attributed by the OS)',
    },
    'osSrc.process.subsystem': {
      type: FIELD_TYPE.TEXT,
      label: 'OS Source Process Subsystem',
      description: 'Subsystem of source process (as attributed by the OS)',
    },
    'osSrc.process.tgtFileCreationCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Target File Creation Count',
      description: 'Count of file creation events',
    },
    'osSrc.process.tgtFileDeletionCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Target File Deletion Count',
      description: 'Count of file deletion events',
    },
    'osSrc.process.tgtFileModificationCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'OS Target File Modification Count',
      description: 'Count of file modification events',
    },
    'osSrc.process.uid': {
      type: FIELD_TYPE.PROCESS,
      label: 'OS Source Process UID',
      description: 'Unique ID of source process (as attributed by the the OS)',
      relatedFields: ['osSrc.process.name'],
      width: 180,
    },
    'osSrc.process.user': {
      type: FIELD_TYPE.USER,
      label: 'OS Source Process User',
      description: 'Username under which source process (attributed by the OS) ran',
    },
    'osSrc.process.verifiedStatus': {
      type: FIELD_TYPE.ENUM,
      label: 'OS Source Process Verified Status',
      description: 'Verification status of process signature (as attributed by the OS)',
      values: ['unverified', 'verified'],
    },
    'registry.keyPath': {
      type: FIELD_TYPE.REGISTRY_KEY,
      label: 'Registry Key Path',
      description: 'Full path location of the Registry Key entry',
    },
    'registry.keyUid': {
      type: FIELD_TYPE.TEXT,
      label: 'Registry Key Unique ID',
      description: 'Unique ID of registry key (as assigned by SentinelOne)',
    },
    'registry.oldValue': {
      type: FIELD_TYPE.TEXT,
      label: 'Registry Old Value',
      description: 'Registry previous value (in case of modification)',
    },
    'registry.oldValueFullSize': {
      type: FIELD_TYPE.NUMBER,
      label: 'Registry Old Value Full Size',
      description: 'Registry previous full size (in case of modification)',
    },
    'registry.oldValueIsComplete': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'Registry Old Value Is Complete',
      description: 'Was the previous registry value full size or was it truncated (in case of modification)',
    },
    'registry.oldValueType': {
      type: FIELD_TYPE.ENUM,
      label: 'Registry Old Value Type',
      description: 'Registry previous value type (in case of modification)',
      values: ['BINARY', 'DWORD', 'DWORD_BIG_ENDIAN', 'EXPAND_SZ', 'LINK', 'MULTI_SZ', 'QWORD', 'RESOURCE_LIST', 'SZ'],
    },
    'registry.value': {
      type: FIELD_TYPE.TEXT,
      label: 'Registry Value',
      description: 'Registry Value',
    },
    'registry.valueFullSize': {
      type: FIELD_TYPE.NUMBER,
      label: 'Registry Value Full Size',
      description: 'Full size of registry value (in case it was truncated)',
    },
    'registry.valueIsComplete': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'Registry Value is Complete',
      description: 'Is the registry value full size or is it truncated',
    },
    'registry.valueType': {
      type: FIELD_TYPE.ENUM,
      label: 'Registry Value Type',
      description: 'Type of registry value',
      values: ['BINARY', 'DWORD', 'DWORD_BIG_ENDIAN', 'EXPAND_SZ', 'LINK', 'MULTI_SZ', 'QWORD', 'RESOURCE_LIST', 'SZ'],
    },
    'site.id': {
      type: FIELD_TYPE.TEXT,
      label: 'Site ID',
      description: 'SentinelOne Site ID',
    },
    'site.name': {
      type: FIELD_TYPE.TEXT,
      label: 'Site Name',
      description: 'SentinelOne Site name',
    },
    'src.endpoint.ip.address': {
      type: FIELD_TYPE.IP_ADDRESS,
      label: 'Source Machine IP',
      description: 'IP of the machine name performing the login attempt',
    },
    'src.ip.address': {
      type: FIELD_TYPE.IP_ADDRESS,
      label: 'Source IP',
      description: 'IP address of traffic source',
    },
    'src.port.number': {
      type: FIELD_TYPE.NUMBER,
      label: 'Source Port',
      description: 'Port number of traffic source',
    },
    'src.process.activeContent.hash': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Active Content Hash',
      description: 'Active Content SHA1 for source process',
    },
    'src.process.activeContent.id': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Active Content File ID',
      description: 'Active Content file unique ID for source process',
    },
    'src.process.activeContent.path': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Active Content Path',
      description: 'Active Content file path for source process',
    },
    'src.process.activeContent.signedStatus': {
      type: FIELD_TYPE.ENUM,
      label: 'Source Process Active Content Signed Status',
      description: 'Active Content file signed status for source process',
      values: ['signed', 'unsigned'],
    },
    'src.process.activeContentType': {
      type: FIELD_TYPE.ENUM,
      label: 'Source Process Active Content Type',
      description: 'Active Content type of source process',
      values: ['CLI', 'FILE'],
    },
    'src.process.childProcCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Child Process Count',
      description: 'Child process count',
    },
    'src.process.cmdline': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Command Line',
      description: 'Command arguments sent with a process',
    },
    'src.process.crossProcessCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Cross Process Count',
      description: 'Target Process event Count',
    },
    'src.process.crossProcessDupRemoteProcessHandleCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Cross Process Duplicate Process Handle Count',
      description: "'Duplicate Process Handle' event count",
    },
    'src.process.crossProcessDupThreadHandleCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Cross Process Duplicate Thread Handle Count',
      description: "'Duplicate Thread Handle' Count",
    },
    'src.process.crossProcessOpenProcessCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Cross Process Open Process Count',
      description: "'Open Process' Count",
    },
    'src.process.crossProcessOutOfStorylineCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Cross Process Out of Storyline Count',
      description: "'Out of Storyline' event count",
    },
    'src.process.crossProcessThreadCreateCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Cross Process Thread Create Count',
      description: "'Remote Thread' Count",
    },
    'src.process.displayName': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Display name',
      description: 'Display name of source process',
    },
    'src.process.dnsCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'DNS Requests Count',
      description: 'Count of DNS requests',
    },
    'src.process.image.binaryIsExecutable': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'Source Process Binary is Executable',
      description: 'Is binary backing source process an executable',
    },
    'src.process.image.md5': {
      type: FIELD_TYPE.HASH,
      label: 'Source Process Image MD5',
      description: 'MD5 signature',
    },
    'src.process.image.path': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Image path',
      description: 'Path name of running process',
    },
    'src.process.image.sha1': {
      type: FIELD_TYPE.HASH,
      label: 'Source Process Image SHA1',
      description: 'SHA1 signature',
    },
    'src.process.image.sha256': {
      type: FIELD_TYPE.HASH,
      label: 'Source Process Image SHA256',
      description: 'SHA256 signature',
    },
    'src.process.indicatorBootConfigurationUpdateCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Indicator Boot Configuration Update Count',
      description: 'Count of indicators - Boot Configuration Update',
    },
    'src.process.indicatorEvasionCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Indicator Evasion Count',
      description: 'Count of indicators - Evasion',
    },
    'src.process.indicatorExploitationCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Indicator Exploitation Count',
      description: 'Count of indicators - Exploitation',
    },
    'src.process.indicatorGeneralCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Indicator General Count',
      description: 'Count of indicators - General',
    },
    'src.process.indicatorInfostealerCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Indicator Infostealer Count',
      description: 'Count of indicators - Infostealer',
    },
    'src.process.indicatorInjectionCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Indicator Injection Count',
      description: 'Count of indicators - Injection',
    },
    'src.process.indicatorPersistenceCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Indicator Persistence Count',
      description: 'Count of indicators - Persistence',
    },
    'src.process.indicatorPostExploitationCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Indicator Post Exploitation Count',
      description: 'Count of indicators - Post Exploitation',
    },
    'src.process.indicatorRansomwareCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Indicator Ransomware Count',
      description: 'Count of indicators - Ransomware',
    },
    'src.process.indicatorReconnaissanceCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Indicator Reconnaissance Count',
      description: 'Count of indicators - Reconnaissance',
    },
    'src.process.integrityLevel': {
      type: FIELD_TYPE.ENUM,
      label: 'Source Process Integrity level',
      description: 'The process integrity level',
      values: ['HIGH', 'INTEGRITY_LEVEL_UNKNOWN', 'LOW', 'MEDIUM', 'SYSTEM', 'UNTRUSTED'],
    },
    'src.process.isNative64Bit': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'Source Process Is Native 64Bit',
      description: 'Indicates if the process is 32bit or 64',
    },
    'src.process.isRedirectCmdProcessor': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'Source Process Is Redirected Command Processor',
      description: 'Indicates if interpreter is with stdout redirection',
    },
    'src.process.isStorylineRoot': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'Source Process Is Storyline Root',
      description: 'Indicates if the process is Root or not',
    },
    'src.process.moduleCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Module Count',
      description: 'Count of Modules Loaded',
    },
    'src.process.name': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Name',
      description: 'Name of source process',
    },
    'src.process.netConnCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Network Connections Count',
      description: 'Network connection count',
    },
    'src.process.netConnInCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Network Connection Incoming Count',
      description: 'Incoming network connection count',
    },
    'src.process.netConnOutCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Network Connection Outgoing Count',
      description: 'Outgoing network connection count',
    },
    'src.process.parent.activeContent.hash': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Parent Active Content Hash',
      description: 'Active Content SHA1 for source process parent',
    },
    'src.process.parent.activeContent.id': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Parent Active Content File ID',
      description: 'Active Content file unique ID for source process parent',
    },
    'src.process.parent.activeContent.path': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Parent Active Content Path',
      description: 'Active Content file path for source process parent',
    },
    'src.process.parent.activeContent.signedStatus': {
      type: FIELD_TYPE.ENUM,
      label: 'Source Process Parent Active Content Signed Status',
      description: 'Active Content file signed status for source process parent',
      values: ['signed', 'unsigned'],
    },
    'src.process.parent.activeContentType': {
      type: FIELD_TYPE.ENUM,
      label: 'Source Process Parent Active Content Type',
      description: 'Active Content type of source process parent',
      values: ['CLI', 'FILE'],
    },
    'src.process.parent.cmdline': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Parent Command Line',
      description: 'Command arguments sent with the process that created this process',
    },
    'src.process.parent.displayName': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Parent Display name',
      description: 'Display name of process that created this process',
    },
    'src.process.parent.image.md5': {
      type: FIELD_TYPE.HASH,
      label: 'Source Process Parent Image MD5',
      description: 'MD5 of process that created this process',
    },
    'src.process.parent.image.path': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Parent Image path',
      description: 'Image path of process that created this process',
    },
    'src.process.parent.image.sha1': {
      type: FIELD_TYPE.HASH,
      label: 'Source Process Parent Image SHA1',
      description: 'SHA1 of process that created this process',
    },
    'src.process.parent.image.sha256': {
      type: FIELD_TYPE.HASH,
      label: 'Source Process Parent Image SHA256',
      description: 'SHA256 of process that created this process',
    },
    'src.process.parent.integrityLevel': {
      type: FIELD_TYPE.ENUM,
      label: 'Source Process Parent Integrity level',
      description: 'Integrity level of process that created this process',
      values: ['HIGH', 'INTEGRITY_LEVEL_UNKNOWN', 'LOW', 'MEDIUM', 'SYSTEM', 'UNTRUSTED'],
    },
    'src.process.parent.isNative64Bit': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'Source Process Parent Is Native 64Bit',
      description: 'Was source process parent compiled natively for 64-Bit or runs as WoW',
    },
    'src.process.parent.isRedirectCmdProcessor': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'Source Process Parent Is Redirected Command Processor',
      description: 'Does source process parent interpreter have stdout redirection',
    },
    'src.process.parent.isStorylineRoot': {
      type: FIELD_TYPE.BOOLEAN,
      label: 'Source Process Parent Is Storyline Root',
      description: 'Is source process parent root of Storyline',
    },
    'src.process.parent.name': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Parent Name',
      description: 'Name of process that created this process',
    },
    'src.process.parent.pid': {
      type: FIELD_TYPE.NUMBER,
      label: 'Source Process Parent PID',
      description: 'PID of process that created this process',
    },
    'src.process.parent.publisher': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Parent Publisher',
      description: 'Publisher that signed binary invoked as part of the process that created this process',
    },
    'src.process.parent.reasonSignatureInvalid': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Parent Reason Signature Invalid',
      description: 'Reason process signature is not valid',
    },
    'src.process.parent.sessionId': {
      type: FIELD_TYPE.NUMBER,
      label: 'Source Process Parent Session ID',
      description: 'ID of terminal (cmd, shell, other) session of source process',
    },
    'src.process.parent.signedStatus': {
      type: FIELD_TYPE.ENUM,
      label: 'Source Process Parent Signed Status',
      description: 'Signature status of process that created this process',
      values: ['signed', 'unsigned'],
    },
    'src.process.parent.startTime': {
      type: FIELD_TYPE.TIMESTAMP,
      label: 'Source Process Parent Start Time',
      description: 'Time process that created this process started to run',
    },
    'src.process.parent.storyline.id': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Parent StoryLine ID',
      description: 'Storyline ID of process that created this process',
    },
    'src.process.parent.uid': {
      type: FIELD_TYPE.PROCESS,
      label: 'Source Process Parent UID',
      description: 'Unique ID of process that created this process',
      relatedFields: [
        'agent.uuid',
        'event.time',
        'mgmt.url',
        'src.process.parent.name',
        'src.process.startTime',
        'src.process.parent.storyline.id',
      ],
      width: 180,
    },
    'src.process.parent.user': {
      type: FIELD_TYPE.USER,
      label: 'Source Process Parent User',
      description: 'Username under which the process that created this process ran',
    },
    'src.process.pid': {
      type: FIELD_TYPE.NUMBER,
      label: 'Source Process ID (PID)',
      description: 'PID of source process',
    },
    'src.process.publisher': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Publisher',
      description: 'Signature sign identity',
    },
    'src.process.reasonSignatureInvalid': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Reason Signature Invalid',
      description: 'Signature not verified reason',
    },
    'src.process.registryChangeCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Registry Change Count',
      description: 'Count of registry key changes',
    },
    'src.process.rpid': {
      type: FIELD_TYPE.NUMBER,
      label: 'Source Process RPID',
      description: 'Real/Relinked PID (after relinking)',
    },
    'src.process.sessionId': {
      type: FIELD_TYPE.NUMBER,
      label: 'Source Process Session ID',
      description: 'The sessions the process runs at',
    },
    'src.process.signedStatus': {
      type: FIELD_TYPE.ENUM,
      label: 'Source Process Signed Status',
      description: 'signed, unsigned',
      values: ['signed', 'unsigned'],
    },
    'src.process.startTime': {
      type: FIELD_TYPE.TIMESTAMP,
      label: 'Source Process Start Time',
      description: 'Start time of source process',
    },
    'src.process.storyline.id': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process StoryLine ID',
      description: 'Storyline ID source process',
    },
    'src.process.subsystem': {
      type: FIELD_TYPE.TEXT,
      label: 'Source Process Subsystem',
      description: 'The subsystem of the process Win32/WSL',
    },
    'src.process.tgtFileCreationCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Target File Creation Count',
      description: 'Count of file creation events',
    },
    'src.process.tgtFileDeletionCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Target File Deletion Count',
      description: 'Count of file deletion events',
    },
    'src.process.tgtFileModificationCount': {
      type: FIELD_TYPE.NUMBER,
      label: 'Target File Modification Count',
      description: 'Count of file modification events',
    },
    'src.process.tid': {
      type: FIELD_TYPE.NUMBER,
      label: 'Source Process Thread ID',
      description: 'Thread id',
    },
    'src.process.uid': {
      label: 'Source Process UID',
      type: FIELD_TYPE.PROCESS,
      description: 'Unique ID of source process',
      relatedFields: [
        'agent.uuid',
        'event.time',
        'mgmt.url',
        'src.process.name',
        'src.process.startTime',
        'src.process.storyline.id',
      ],
      width: 180,
    },
    'src.process.user': {
      type: FIELD_TYPE.USER,
      label: 'Source Process User',
      description: 'Username under which source process ran',
    },
    'tgt.file.path': {
      type: FIELD_TYPE.FILE,
      label: 'Target File Path',
    },
    'tgt.file.size': {
      type: FIELD_TYPE.TEXT,
      label: 'Target File size',
    },
    'tgt.file.type': {
      type: FIELD_TYPE.TEXT,
      label: 'Target File Type',
    },
    'tgt.process.cmdline': {
      type: FIELD_TYPE.TEXT,
      label: 'Target Process Command Line',
    },
    'tgt.process.displayName': {
      type: FIELD_TYPE.TEXT,
      label: 'Target Process Display name',
    },
    'tgt.process.name': {
      type: FIELD_TYPE.TEXT,
      label: 'Target Process Name',
    },
    'tgt.process.relation': {
      type: FIELD_TYPE.TEXT,
      label: 'Target Process Relation',
    },
    'tgt.process.uid': {
      type: FIELD_TYPE.PROCESS,
      label: 'Target Process UID',
      relatedFields: [
        'agent.uuid',
        'event.time',
        'mgmt.url',
        'tgt.process.name',
        'tgt.process.startTime',
        'tgt.process.storyline.id',
      ],
      width: 180,
    },
    'task.name': {
      type: FIELD_TYPE.TASK,
      label: 'Task name',
    },
    'url.address': {
      type: FIELD_TYPE.URL,
      label: 'URL',
    },
    'event.mitreTechnique.id': {
      label: '',
      description: 'The MITRE Technique id associated with the event',
      type: FIELD_TYPE.TEXT,
    },
    'alert.ruleId': {
      label: 'Rule ID',
      description: 'The ID of the rule generating the alert',
      type: FIELD_TYPE.TEXT,
    },
    'tgt.process.image.sha1': {
      label: 'Target Process Image SHA1',
      description: 'SHA1  of target process',
      type: FIELD_TYPE.HASH,
    },
    'actor.alternateId': {
      label: 'Actor Alternate ID',
      description: 'Alternative ID of the actor',
      type: FIELD_TYPE.TEXT,
    },
    'tgt.file.sha1': {
      label: 'Target File SHA1',
      description: 'SHA1 Signature of File',
      type: FIELD_TYPE.HASH,
    },
    'tgt.process.subsystem': {
      label: 'Target Process Subsystem',
      description: 'Subsystem of target process',
      type: FIELD_TYPE.TEXT,
    },
    'tgt.file.oldSha1': {
      label: 'Target File Old SHA1',
      description: 'Old file SHA1 before Modify',
      type: FIELD_TYPE.HASH,
    },
    'namedPipe.remoteClients': {
      label: 'Named Pipe Remote Clients Mode',
      description: 'Indication of the pipe type (local or remote)',
      type: FIELD_TYPE.ENUM,
      values: ['Accept', 'Reject', 'Unknown'],
    },
    'tgt.process.sessionId': {
      label: 'Target Process Session ID',
      description: 'ID of the terminal (cmd, shell, other) session of target process',
      type: FIELD_TYPE.NUMBER,
    },
    'namedPipe.securityOwner': {
      label: 'Named Pipe Security Descriptor Owner',
      description: 'The named pipe Security Descriptor owner',
      type: FIELD_TYPE.TEXT,
    },
    'event.externalID': {
      label: 'Event External ID',
      description: 'The ID of the event as was associated by the Vendor',
      type: FIELD_TYPE.TEXT,
    },
    'dataSource.category': {
      label: 'Data Source Category',
      description: 'Classification of the source sending the data to S1 Singulatiy XDR. Could be multiple values',
      type: FIELD_TYPE.TEXT,
    },
    'cmdScript.originalSize': {
      label: 'Source Process Command Script Original Size',
      description: 'Original command script size (in Bytes)',
      type: FIELD_TYPE.NUMBER,
    },
    'tiIndicator.externalId': {
      label: 'TI Indicator External ID',
      description:
        'The unique identifier of the Threat Intelligence indicator as provided by the Threat Intelligence source',
      type: FIELD_TYPE.TEXT,
    },
    'tgt.process.image.binaryIsExecutable': {
      label: 'Target Process Binary is Executable',
      description: 'Is binary backing the target process an executable',
      type: FIELD_TYPE.BOOLEAN,
    },
    'tgt.file.md5': {
      label: 'Target File MD5',
      description: 'MD5 Signature of File',
      type: FIELD_TYPE.HASH,
    },
    'tgt.process.pid': {
      label: 'Target Process ID (PID)',
      description: 'PID for target process',
      type: FIELD_TYPE.NUMBER,
    },
    'namedPipe.typeMode': {
      label: 'Named Pipe Type Mode',
      description: 'The pipe type mode',
      type: FIELD_TYPE.ENUM,
      values: ['Byte', 'Message', 'Unknown'],
    },
    'cmdScript.sha256': {
      label: 'Source Process Command Script SHA256',
      description: 'SHA256 of command script (for exclusions)',
      type: FIELD_TYPE.HASH,
    },
    'tgt.process.activeContent.signedStatus': {
      label: 'Target Process Active Content Signed Status',
      description: 'Active Content file signed status for target process',
      type: FIELD_TYPE.ENUM,
      values: ['signed', 'unsigned'],
    },
    'tiIndicator.type': {
      label: 'TI Indicator Type',
      description: 'The type of the identified Threat Intelligence indicator',
      type: FIELD_TYPE.ENUM,
      values: ['Sha1', 'Sha256', 'Md5', 'IPv4', 'IPv6', 'DNS', 'URL'],
    },
    'tgt.process.publisher': {
      label: 'Target Process Publisher',
      description: 'Publisher that digitally signed the binary being invoked as part of the target process',
      type: FIELD_TYPE.TEXT,
    },
    'tgt.process.isRedirectCmdProcessor': {
      label: 'Target Process Is Redirected Command Processor',
      description: 'Is interpreter for target process with stdout redirection',
      type: FIELD_TYPE.BOOLEAN,
    },
    'tgt.file.convictedBy': {
      label: 'Target File Convicted by',
      description: 'Reputation, Static AI',
      type: FIELD_TYPE.ENUM,
      values: ['Reputation', 'Static AI'],
    },
    'namedPipe.connectionType': {
      label: 'Named Pipe Connection Type',
      description: 'The pipe connection type',
      type: FIELD_TYPE.ENUM,
      values: ['Local', 'Remote', 'Unknown'],
    },
    'event.mitreTactic': {
      label: 'Event MITRE Tactic',
      description: 'The MITRE Tactic associated with the event',
      type: FIELD_TYPE.TEXT,
    },
    'tiIndicator.metadata': {
      label: 'TI Indicator Metadata',
      description: 'The metadata of the identified Threat Intelligence indicator',
      type: FIELD_TYPE.TEXT,
    },
    'agent.ip': {
      label: 'Agent IP',
      description: 'The IP of the vendor agent',
      type: FIELD_TYPE.TEXT,
    },
    'tiIndicator.comparisonMethod': {
      label: 'TI Indicator comparison method',
      description: 'The comparison method used by SentinelOne to trigger the event',
      type: FIELD_TYPE.ENUM,
      values: ['EQUALS'],
    },
    'tgt.process.isNative64Bit': {
      label: 'Target Process Is Native 64Bit',
      description: 'Is target process compiled natively for 64-Bit or runs as WoW',
      type: FIELD_TYPE.BOOLEAN,
    },
    'tgt.process.image.md5': {
      label: 'Target Process Image MD5',
      description: 'MD5  of target process',
      type: FIELD_TYPE.HASH,
    },
    'tgt.file.isExecutable': {
      label: 'Target File Is Executable',
      description: 'Is file executable',
      type: FIELD_TYPE.BOOLEAN,
    },
    'tiIndicator.creationTime': {
      label: 'TI Indicator Creation Time',
      description:
        'The time at which the Threat Intelligence indicator was originally created (as indicated by the TI source)',
      type: FIELD_TYPE.TIMESTAMP,
    },
    'tgt.file.oldPath': {
      label: 'Target File Old File Path',
      description: "Old path before 'Rename'",
      type: FIELD_TYPE.TEXT,
    },
    'tgt.displayName': {
      label: 'Target Display Name',
      description:
        'Target is the entity upon which an actor performs an action. The target display name is the name of target used for display purposes',
      type: FIELD_TYPE.TEXT,
    },
    'namedPipe.securityGroups': {
      label: 'Named Pipe Security Descriptor Group',
      description: 'The named pipe Security Descriptor group',
      type: FIELD_TYPE.TEXT,
    },
    'tgt.file.extension': {
      label: 'Target File Extension',
      description: 'File Extension',
      type: FIELD_TYPE.TEXT,
    },
    'tiIndicator.intrusionSets': {
      label: 'TI Indicator intrusion sets',
      description:
        'The intrusion sets associated with the Threat Intelligence indicator. That is, a grouped set of adversarial behaviors and resources with common properties that is believed to be orchestrated by a single organization',
      type: FIELD_TYPE.ARRAY,
    },
    'cmdScript.applicationName': {
      label: 'Source Process Command Script Application Name',
      description: 'Name of application that ran command script',
      type: FIELD_TYPE.TEXT,
    },
    'namedPipe.name': {
      label: 'Named Pipe Name',
      description: 'The unique pipe name',
      type: FIELD_TYPE.TEXT,
    },
    'tgt.file.oldMd5': {
      label: 'Target File Old MD5',
      description: 'Old file MD5 before Modify',
      type: FIELD_TYPE.HASH,
    },
    'tgt.file.oldSha256': {
      label: 'Target File Old SHA256',
      description: 'Old file SHA256 before Modify',
      type: FIELD_TYPE.HASH,
    },
    'event.repetitionCount': {
      label: 'Event Repetition Count',
      description: 'Count of Concurrent Identical Events',
      type: FIELD_TYPE.NUMBER,
    },
    'tiIndicator.source': {
      label: 'TI Source',
      description: 'The source of the identified Threat Intelligence indicator',
      type: FIELD_TYPE.TEXT,
    },
    'agent.version': {
      label: 'Agent Version',
      description: 'Version of SentinelOne Agent',
      type: FIELD_TYPE.TEXT,
    },
    'src.process.verifiedStatus': {
      label: 'Source Process Verified Status',
      description: 'verified, unverified',
      type: FIELD_TYPE.ENUM,
      values: ['verified', 'unverified'],
    },
    'tgt.file.description': {
      label: 'Target File Description',
      description: 'Description of file',
      type: FIELD_TYPE.TEXT,
    },
    'event.mitreTechnique.name': {
      label: 'Event MITRE Technique Name',
      description: 'The MITRE Technique associated with the event',
      type: FIELD_TYPE.TEXT,
    },
    'tgt.file.id': {
      label: 'Target File Id',
      description: 'Unique ID of file',
      type: FIELD_TYPE.TEXT,
    },
    'event.classification': {
      label: 'Event Classification',
      description: 'The behaviour associated with the given event',
      type: FIELD_TYPE.TEXT,
    },
    'namedPipe.waitMode': {
      label: 'Named Pipe Wait Mode',
      description: 'The pipe wait mode',
      type: FIELD_TYPE.ENUM,
      values: ['Blocking Wait', 'Non-Blocking Wait', 'Unknown'],
    },
    'namedPipe.maxInstances': {
      label: 'Named Pipe Max Instances',
      description: 'The max instances of a pipe',
      type: FIELD_TYPE.NUMBER,
    },
    'event.status': {
      label: 'Event Status',
      description: 'Whether the detection has been triaged yes or not',
      type: FIELD_TYPE.BOOLEAN,
    },
    'namedPipe.isFirstInstance': {
      label: 'Named Pipe is First Instance',
      description: 'Is named pipe created with First Instance flag',
      type: FIELD_TYPE.BOOLEAN,
    },
    'tiIndicator.description': {
      label: 'TI Indicator Description',
      description: 'The description of the identified Threat Intelligence indicator',
      type: FIELD_TYPE.TEXT,
    },
    'endpoint.macAddress': {
      label: 'Endpoint Mac Address',
      description: 'Endpoint Mac Address',
      type: FIELD_TYPE.TEXT,
    },
    'task.path': {
      label: 'Task Path',
      description: 'Full path location of a scheduled task',
      type: FIELD_TYPE.TEXT,
    },
    'tgt.process.reasonSignatureInvalid': {
      label: 'Target Process Reason Signature Invalid',
      description: 'Indicates the reason the target process signature is not valid',
      type: FIELD_TYPE.TEXT,
    },
    'tgt.file.creationTime': {
      label: 'Target File Created At',
      description: 'Date and Time of File Creation, format: Month Day, Year hour:minute:second',
      type: FIELD_TYPE.TIMESTAMP,
    },
    'tiIndicator.categories': {
      label: 'TI Indicator Categories',
      description:
        'The categories of the identified Threat Intelligence indicator, e.g. the malware type associated with the indicator',
      type: FIELD_TYPE.ARRAY,
    },
    'tiIndicator.modificationTime': {
      label: 'TI Indicator Modification Time',
      description: 'The time at which the Threat Intelligence indicator was last updated in SentinelOne DB',
      type: FIELD_TYPE.TIMESTAMP,
    },
    'event.externalThreatValue': {
      label: 'External Threat Value',
      description: 'The threat score of the event as sent by the external source',
      type: FIELD_TYPE.TEXT,
    },
    'actor.id': {
      label: 'Actor ID',
      description: 'The unique key of the user, app, client, or other entity who performs an action on a target',
      type: FIELD_TYPE.TEXT,
    },
    'namedPipe.accessMode': {
      label: 'Named Pipe Access Mode',
      description: 'The pipe access mode',
      type: FIELD_TYPE.ENUM,
      values: ['Access Duplex', 'Access Inbound', 'Access Outbound', 'Unknown'],
    },
    'tgt.process.signedStatus': {
      label: 'Target Process Signed Status',
      description: 'Signature status of target process',
      type: FIELD_TYPE.ENUM,
      values: ['signed', 'unsigned'],
    },
    'alert.name': {
      label: 'Alert Name',
      description: 'The name of the generated alert',
      type: FIELD_TYPE.TEXT,
    },
    'NamedPipe.isWriteThrough': {
      label: 'Named Pipe is Write-through',
      description: 'Is named pipe created with Write-through flag',
      type: FIELD_TYPE.BOOLEAN,
    },
    'tgt.file.location': {
      label: 'Target File location',
      description: 'Location of file',
      type: FIELD_TYPE.ENUM,
      values: ['Local', 'Network Drive', 'External Device'],
    },
    'tgt.file.sha256': {
      label: 'Target File SHA256',
      description: 'SHA256 Signature of File',
      type: FIELD_TYPE.HASH,
    },
    'endpoint.id': {
      label: 'Endpoind ID',
      description: 'Endpoint ID',
      type: FIELD_TYPE.TEXT,
    },
    'tiIndicator.references': {
      label: 'TI Indicator reference',
      description: 'External reference associated with the Threat Intelligence indicator',
      type: FIELD_TYPE.ARRAY,
    },
    'tgt.process.activeContentType': {
      label: 'Target Process Active Content Type',
      description: 'Active Content type of target process',
      type: FIELD_TYPE.ENUM,
      values: ['CLI', 'FILE'],
    },
    'tgt.process.activeContent.id': {
      label: 'Target Process Active Content File ID',
      description: 'Active Content file unique ID for target process',
      type: FIELD_TYPE.TEXT,
    },
    'tgt.file.internalName': {
      label: 'Target File Internal Name',
      description: 'Internal name of file',
      type: FIELD_TYPE.TEXT,
    },
    'event.outcome.result': {
      label: 'Event Outcome',
      description: 'The outcome of an event',
      type: FIELD_TYPE.ENUM,
      values: [''],
    },
    'tgt.file.isSigned': {
      label: 'Target File Is Signed',
      description: 'Is file signed',
      type: FIELD_TYPE.ENUM,
      values: ['signed', 'unsigned'],
    },
    'tgt.process.dataIncompleteReason': {
      label: 'Target Process Data Incomplete Reason',
      description: 'Reason that target process is incomplete',
      type: FIELD_TYPE.ARRAY,
    },
    'actor.privilege': {
      label: 'Actor privilege',
      description: 'Whether the acting entity is with privileged rights or not',
      type: FIELD_TYPE.BOOLEAN,
    },
    'tiIndicator.name': {
      label: 'TI Indicator Name',
      description: 'The name of the identified Threat Intelligence indicator',
      type: FIELD_TYPE.TEXT,
    },
    'tiIndicator.value': {
      label: 'TI Indicator Value',
      description: 'The value of the identified Threat Intelligence indicator',
      type: FIELD_TYPE.TEXT,
    },
    'event.startTime': {
      label: 'Event Start Time',
      description: 'The start time of the event',
      type: FIELD_TYPE.TIMESTAMP,
    },
    'tiIndicator.mitreTactics': {
      label: 'TI Indicator MITRE tactics',
      description:
        'The MITRE tactics associated with the Threat Intelligence indicator - indicates the malicious behavior at that phase of the kill chain',
      type: FIELD_TYPE.ARRAY,
    },
    'tiIndicator.addedBy': {
      label: 'TI Indicator added by',
      description: 'The user uploaded the Threat Intelligence indicator',
      type: FIELD_TYPE.TEXT,
    },
    'event.endTime': {
      label: 'Event End Time',
      description: 'The end time of the event',
      type: FIELD_TYPE.TIMESTAMP,
    },
    'namedPipe.readMode': {
      label: 'Named Pipe Read Mode',
      description: 'The pipe read mode',
      type: FIELD_TYPE.ENUM,
      values: ['Byte', 'Message', 'Unknown'],
    },
    'tgt.process.storyline.id': {
      label: 'Target Process StoryLine ID',
      description: 'Storyline ID of target event',
      type: FIELD_TYPE.TEXT,
    },
    'endpoint.domain': {
      label: 'Endpoint Domain',
      description:
        'This reflects the domain name where the endpoint belongs. Domain name mostly will be local for for AD Domains.',
      type: FIELD_TYPE.TEXT,
    },
    'tiIndicator.validUntil': {
      label: 'TI Indicator Valid Until',
      description: 'The date from which the Threat Intelligence indicator will no longer be monitored',
      type: FIELD_TYPE.TIMESTAMP,
    },
    'tiIndicator.threatActors': {
      label: 'TI Indicator threat actors',
      description:
        'The threat actors associated with the Threat Intelligence indicator. Threat Actors are actual individuals, groups, or organizations believed to be operating with malicious intent.',
      type: FIELD_TYPE.ARRAY,
    },
    'alert.description': {
      label: 'Alert Description',
      description: 'The description of the generated alert',
      type: FIELD_TYPE.TEXT,
    },
    'alert.eventIds': {
      label: 'Alert Related Event IDs',
      description: 'A list of all associated events to a given alert',
      type: FIELD_TYPE.ARRAY,
    },
    'NamedPipe.isOverlapped': {
      label: 'Named Pipe Overlapped',
      description: 'Is named pipe created with Overlapped',
      type: FIELD_TYPE.BOOLEAN,
    },
    'event.certainty': {
      label: 'Event Certainty',
      description: 'The certainty of the score assigned to this host',
      type: FIELD_TYPE.NUMBER,
    },
    'tgt.process.activeContent.path': {
      label: 'Target Process Active Content Path',
      description: 'Active Content file path of source process was the target of the event',
      type: FIELD_TYPE.TEXT,
    },
    'cmdScript.content': {
      label: 'Source Process Command Script',
      description: 'Command script executed through source process',
      type: FIELD_TYPE.TEXT,
    },
    'tiIndicator.uploadTime': {
      label: 'TI Indicator Upload Time',
      description: 'The time at which the Threat Intelligence indicator was uploaded to SentinelOne DB',
      type: FIELD_TYPE.TIMESTAMP,
    },
    'tgt.process.user': {
      label: 'Target Process User',
      description: 'Username under which target process ran',
      type: FIELD_TYPE.TEXT,
    },
    'tgt.process.accessRights': {
      label: 'Target Process Access Rights',
      description: 'Type of access granted to process by cross process',
      type: FIELD_TYPE.NUMBER,
    },
    'agent.name': {
      label: 'Agent Name',
      description: 'The hostname of the vendor agent',
      type: FIELD_TYPE.TEXT,
    },
    'tgt.process.verifiedStatus': {
      label: 'Target Process Verified Status',
      description: 'Verification status of signature of the target process e.g. : Verified, Unverified',
      type: FIELD_TYPE.ENUM,
      values: ['verified', 'unverified'],
    },
    'event.outcome.reason': {
      label: 'Event Outcome Reason',
      description: 'Reason for the result, for example, INVALID_CREDENTIALS',
      type: FIELD_TYPE.ENUM,
      values: [''],
    },
    'tgt.file.modificationTime': {
      label: 'Target File Modified At',
      description: 'Date and time file was modified, format: Month Day, Year hour:minute:second',
      type: FIELD_TYPE.TIMESTAMP,
    },
    'tiIndicator.uid': {
      label: 'TI Indicator UID',
      description: 'The unique identifier of the Threat Intelligence indicator as provided by SentinelOne',
      type: FIELD_TYPE.ID,
    },
    'tgt.process.image.sha256': {
      label: 'Target Process Image SHA256',
      description: 'SHA256  of target process',
      type: FIELD_TYPE.HASH,
    },
    'cmdScript.isComplete': {
      label: 'Source Process Command Script Is Complete',
      description: 'Is command script fully available or truncated',
      type: FIELD_TYPE.BOOLEAN,
    },
    'tgt.process.startTime': {
      label: 'Target Process Start Time',
      description: 'Start time of target process, format: Month Day, Year hour:minute:second',
      type: FIELD_TYPE.TIMESTAMP,
    },
    'tgt.process.activeContent.hash': {
      label: 'Target Process Active Content Hash',
      description: 'Active Content SHA1 for target process',
      type: FIELD_TYPE.TEXT,
    },
    'tgt.process.isStorylineRoot': {
      label: 'Target Process Is Storyline Root',
      description: 'Is target process root of Storyline',
      type: FIELD_TYPE.BOOLEAN,
    },
    'event.externalLink': {
      label: 'External Link',
      description: 'The link to the original Alert ingested',
      type: FIELD_TYPE.TEXT,
    },
    'actor.displayName': {
      label: 'Actor Display Name',
      description: 'Display name of the actor',
      type: FIELD_TYPE.TEXT,
    },
    'tgt.process.integrityLevel': {
      label: 'Target Process Integrity level',
      description: 'Integrity level of target process',
      type: FIELD_TYPE.ENUM,
      values: ['HIGH', 'MEDIUM', 'LOW', 'SYSTEM', 'INTEGRITY_LEVEL_UNKNOWN', 'UNTRUSTED'],
    },
    'tgt.process.image.path': {
      label: 'Target Process Image path',
      description: 'Image path of target process',
      type: FIELD_TYPE.TEXT,
    },
  },
  categoryField: EVENT_CATEGORY_FIELD,
  summaryFieldIdsByCategory: SUMMARY_FIELDS_BY_CATEGORY,
};
